/* eslint-disable camelcase */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { executeHttpRequest } from '../../epics/api';
import { getErrorMessages } from '../../utils';
import { SERVER_URL } from '../../config';
import { TClientData, TPageNotification } from '../../types';

type TFields = {
    mobile: string;
    billing_email: string;
    billing_address_1: string;
    billing_address_2: string;
    billing_city: string;
    billing_postcode: string;
}

type TClientDetails = {
    clientId: string;
    clientData: TClientData;
    clientCategories: { value: string; label: string }[];
    isSuperUser: boolean;
    notification: TPageNotification;
    setClientData: (clientData: TClientData) => void;
}

export default function ClientDetails({
    clientId,
    clientData,
    clientCategories,
    isSuperUser,
    notification,
    setClientData,
}: TClientDetails) {
    const [error, setError] = useState<string | null>(null);
    const [fields, setFields] = useState<TFields | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleTextFieldChange = (e: any) => {
        setFields({
            ...fields,
            [e.target.name]: e.target.value,
        });
    };

    const handleSaveButtonClick = () => {
        setIsLoading(true);
        setError(null);

        const payload = new FormData();
        Object.entries(fields)
            .filter(([key, value]: [keyof TFields, string]) => value !== clientData[key])
            .forEach(([key, value]) => payload.append(key, value));

        executeHttpRequest(`clients/${clientId}/`, 'patch', payload)
            .then(response => {
                setIsLoading(false);
                setIsEditing(false);
                setClientData(response.data.content);
                notification.success('Success', 'Client updated!');
            })
            .catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
            });
    };

    const handleCancelButtonClick = () => {
        setError(null);
        setFields(null);
        setIsEditing(false);
    };

    const handleEditButtonClick = () => {
        setFields({
            mobile: clientData.mobile,
            billing_email: clientData.billing_email,
            billing_address_1: clientData.billing_address_1,
            billing_address_2: clientData.billing_address_2,
            billing_city: clientData.billing_city,
            billing_postcode: clientData.billing_postcode,
        });
        setIsEditing(true);
    };

    const renderSectionButtons = () => (
        isEditing ? (
            <div className="freelancer-details-section__btns">
                <button
                    type="button"
                    disabled={isLoading}
                    className="freelancer-details-section__btn"
                    onClick={handleSaveButtonClick}
                >
                    Save
                </button>
                <button
                    data-testid="cancelBtn"
                    type="button"
                    disabled={isLoading}
                    className="freelancer-details-section__btn"
                    onClick={handleCancelButtonClick}
                >
                    Cancel
                </button>
            </div>
        ) : (
            <div className="freelancer-details-section__btns">
                {isSuperUser && (
                    <Link
                        className="freelancer-details-section__btn"
                        style={{ color: '#000' }}
                        to={{ pathname: `${SERVER_URL}/admin/client/client/${clientId}` }}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <i className="icon icon-pencil-3"/>
                        Back-office
                    </Link>
                )}
                <button
                    data-testid="editBtn"
                    type="button"
                    className="freelancer-details-section__btn"
                    onClick={handleEditButtonClick}
                >
                    <i className="icon icon-pencil-3"/>
                    Edit
                </button>
            </div>
        )
    );

    const renderTextFieldRow = (
        fieldName: keyof TFields,
        humanReadable: string,
        comment?: string,
    ) => (
        <tr>
            <td className="table-default__cell--text-blue">
                <span>{humanReadable}</span>
                {isEditing && comment && (
                    <>
                        <br/>
                        <span className="edit-profile-comment">{comment}</span>
                    </>
                )}
            </td>
            <td>
                {isEditing ? (
                    <input
                        type="text"
                        name={fieldName}
                        value={fields[fieldName]}
                        onChange={handleTextFieldChange}
                    />
                ) : (
                    clientData[fieldName]
                )}
            </td>
        </tr>
    );

    return (
        <div className="freelancer-details-section">
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__title">Client details</div>
                {renderSectionButtons()}
            </div>
            <div>
                {error &&
                    <div className="freelancer-details-section__errors">
                        <p>{error}</p>
                    </div>
                }
            </div>
            <div className="freelancer-details-section__content">
                <table className="table-default">
                    <tbody>
                    <tr>
                        <td className="table-default__cell--text-blue">
                            <span>Company name</span>
                        </td>
                        <td>{clientData.company_name}</td>
                    </tr>
                    <tr>
                        <td className="table-default__cell--text-blue">
                            <span>Name</span>
                        </td>
                        <td>{clientData.first_name} {clientData.last_name}</td>
                    </tr>
                    <tr>
                        <td className="table-default__cell--text-blue">Category of the business</td>
                        <td>{clientCategories.find(cat => cat.value === clientData.category)?.label}</td>
                    </tr>
                    <tr>
                        <td className="table-default__cell--text-blue">
                            <span>E-mail address</span>
                        </td>
                        <td>{clientData.email}</td>
                    </tr>
                    {renderTextFieldRow('mobile', 'Telephone number')}
                    </tbody>
                </table>
            </div>
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__subtitle">Billing contact details</div>
            </div>
            <div className="freelancer-details-section__content">
                <table className="table-default">
                    <tbody>
                        <tr>
                            <td className="table-default__cell--text-blue">
                                <span>Legal name</span>
                            </td>
                            <td>{clientData.legal_name}</td>
                        </tr>
                        <tr>
                            <td className="table-default__cell--text-blue">
                                <span>E-mail addresses</span>
                                {isEditing && (
                                    <>
                                        <br/>
                                        <span className="edit-profile-comment">
                                            Invoices will be sent to these email addresses, one per line
                                        </span>
                                    </>
                                )}
                            </td>
                            {isEditing ? (
                                <td>
                                    <textarea
                                        name="billing_email"
                                        value={fields.billing_email}
                                        rows={3}
                                        onChange={handleTextFieldChange}
                                    />
                                </td>
                            ) : (
                                <td>{clientData.billing_email}</td>
                            )}
                        </tr>
                        {renderTextFieldRow('billing_address_1', 'Address line 1')}
                        {renderTextFieldRow('billing_address_2', 'Address line 2')}
                        {renderTextFieldRow('billing_city', 'City')}
                        {renderTextFieldRow('billing_postcode', 'Postcode')}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
